import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { FieldEditContainerComponent } from '@shared/components/dynamic-form/components/field-edit-container/field-edit-container.component';
import { DynamicField } from '@shared/components/dynamic-form/models/field';
import { DynamicFormService } from '@shared/components/dynamic-form/services/dynamic-form.service';
import { FIELD_TYPE } from '@shared/constants/field-type.const';
import { ScoreFormatPipe } from '@shared/pipes/score-format.pipe';
import { Subscription } from 'rxjs';
import { FormScore } from '../../models/field-base';
import { FormFieldComponent } from '../form-field/form-field.component';

@Component({
  selector: 'app-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  providers: [DynamicFormService],
  imports: [
    FormFieldComponent,
    ReactiveFormsModule,
    DragDropModule,
    NgbModalModule,
    CommonModule,
    FieldEditContainerComponent,
    ScoreFormatPipe,
    TranslateModule,
  ],
})
export class DynamicFormComponent implements AfterViewInit {
  fields = input<DynamicField[]>([]);
  inEditorMode = input<boolean>(false);
  submitBtnDisplayed = input<boolean>(true);
  containerClass = input<string>('');
  errorsDisplayedOnSave = input<boolean>(false);
  totalScoreDisplayed = input<boolean>(false);
  submitText = input<string>('Submit');
  formValue = input<Record<string, unknown>>();
  disabledFields = input<string[]>([]);

  submitted = output<Record<string, unknown>>();
  valueChanged = output<unknown>();
  formValidityChanged = output<boolean>();
  scoreChanged = output<FormScore>();

  formSubmitted = signal(false);
  form = computed<FormGroup>(() =>
    this.dynamicFormService.toFormGroup(this.fields())
  );
  score = signal<FormScore>(null);

  readonly FieldType = FIELD_TYPE;
  private readonly dynamicFormService = inject(DynamicFormService);
  private formValueChangesSubscription: Subscription | null = null;

  constructor() {
    effect(() => {
      const currentForm = this.form();

      this.formValueChangesSubscription?.unsubscribe();
      this.formValidityChanged.emit(currentForm.valid);
      this.formValueChangesSubscription = currentForm.valueChanges.subscribe(
        () => {
          this.valueChanged.emit(currentForm.getRawValue());
          this.formValidityChanged.emit(currentForm.valid);
          this.score.set(this.dynamicFormService.getFormScore(currentForm));
          this.scoreChanged.emit(this.score());
        }
      );

      if (this.form()) {
        this.form().patchValue(this.formValue());
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.form()) {
      for (const fieldName of this.disabledFields()) {
        this.form().get(fieldName)?.disable();
      }
    }
  }

  onSubmit() {
    const formValue = this.form().getRawValue();
    this.submitted.emit(formValue);
    this.formSubmitted.set(true);
  }
}
